import React, { useEffect, useRef, useState } from "react"

import { Grid, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, Collapse, Button } from "@mui/material"
import { makeStyles } from "@mui/styles";

import ArrowDropDownIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from "@mui/icons-material/ChevronRight";

import BlockUi from "react-block-ui";
import "react-block-ui/style.css";
import { WBLabel } from "./WBLabel";

const useRowStyles = makeStyles({
    root: {
      '& > *': {
        borderBottom: 'unset',
      },
    },
});

function WBTable ({
    columns, // [{label: , customLabel: }]
    rows,
    valueCustomator,
    totalData,
    page = 0,
    pageChange,
    rowsPerPage = 20,
    onRowsPerPageChange,
    loading = false,
    infiniteLoad = false,
    hasMore = false,
    loadingMore = false,
    onLoadMore,
    collapse = false,
    emptyMessage = "No data matches the filter",
    detail
}) {

    const loadMoreRef = useRef(null)

    useEffect(() => {
        if (!infiniteLoad || !hasMore || loading || loadingMore || !onLoadMore) {
            return
        }

        const currentRef = loadMoreRef.current
        const observer = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting) {
                onLoadMore()
            }
        }, { rootMargin: '160px' })

        if (currentRef) {
            observer.observe(currentRef)
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef)
            }
            observer.disconnect()
        }
    }, [infiniteLoad, hasMore, loading, loadingMore, onLoadMore])

    const onChangePage = (e) => {
        pageChange(e)
    }

    return (
        <Paper sx={{pb: 2}}>
            <BlockUi tag="div" blocking={loading}>
                <TableContainer>
                    <Table aria-label="simple table">
                        <TableHead>
                            <TableRow>
                                {
                                    columns.map((value, index) => {
                                        return <TableCell key={value.label || index}><WBLabel bold>{value.label}</WBLabel></TableCell>
                                    })
                                }
                            </TableRow>
                        </TableHead>
                        <TableBody>
                        {
                            rows.length === 0 && !loading &&
                            <TableRow>
                                <TableCell colSpan={columns.length} align="center">
                                    <WBLabel>{emptyMessage}</WBLabel>
                                </TableCell>
                            </TableRow>
                        }
                        {
                            rows.map((row) => (
                                <WBTableRow key={row._id} row={row} collapse={collapse} valueCustomator={valueCustomator} detail={detail}/>
                            ))
                        }
                        </TableBody>
                    </Table>
                </TableContainer>
            </BlockUi>
            {
                infiniteLoad ?
                <Grid ref={loadMoreRef} container justifyContent="center" sx={{ p: 2 }}>
                    {
                        loadingMore &&
                        <WBLabel>Loading more...</WBLabel>
                    }
                    {
                        !loadingMore && hasMore &&
                        <Button variant="outlined" onClick={onLoadMore}>Load more</Button>
                    }
                    {
                        !loadingMore && !hasMore && rows.length > 0 &&
                        <WBLabel>All data loaded</WBLabel>
                    }
                </Grid> :
                <TablePagination
                    rowsPerPageOptions={onRowsPerPageChange ? [5, 10, 20, 50] : [rowsPerPage]}
                    component="div"
                    count={totalData ? totalData : rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(event, page) => { onChangePage(page) }}
                    onRowsPerPageChange={(event) => {
                        if (onRowsPerPageChange) { onRowsPerPageChange(event.target.value) }
                    }}
                />
            }
        </Paper>
    )
}

function WBTableRow ({
    row,
    collapse,
    valueCustomator,
    detail
}) {

    const [ open, setOpen ] = useState(false)
    const classes = useRowStyles();
    const rowValues = valueCustomator(row)

    return (
        <React.Fragment key={row._id}>
            <TableRow
                className={collapse ? classes.root : ''}
                key={row._id}
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
            >
                {
                    collapse &&
                    <TableCell padding={"checkbox"}>
                        <div style={{
                            cursor: 'pointer',
                        }} onClick={() => setOpen(!open)}>{open ? <ArrowDropDownIcon/> : <ArrowForwardIcon/>}</div>
                    </TableCell>
                }
                {
                    Object.keys(rowValues).map(value => {
                        return <TableCell key={value} sx={{ minWidth: '120px' }}>{rowValues[value]}</TableCell>
                    })
                }
            </TableRow>
            {
                collapse &&
                <TableRow>
                    <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={12}>
                        <Collapse in={open} timeout="auto" unmountOnExit>
                            {detail(row)}
                        </Collapse>
                    </TableCell>
                </TableRow>
            }
        </React.Fragment>
    )
}

export default WBTable
